import React from 'react';
import { InfiniteData, InfiniteQueryObserverResult } from 'react-query';

import { SecondaryButton } from '@components/Button/variants';
import CustomSpinner, { CustomSpinnerColor } from '@components/Spinner/CustomSpinner';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, click } from '@tracking';

import UsersTable from '.';
import { GroupUser, PaginatedUsers, UserOperationType, UserType } from '../../types';
import UserRow from './UserRow';

interface PaginatedTableProps {
  data: InfiniteData<PaginatedUsers>;
  fetchNextPage: () => Promise<InfiniteQueryObserverResult<PaginatedUsers, unknown>>;
  hasNextPage?: boolean;
  isLoading: boolean;
  onUserClick: (user: GroupUser, operation: UserOperationType) => void;
  userType: UserType;
}

const PaginatedTable = ({
  data,
  fetchNextPage,
  hasNextPage,
  isLoading,
  onUserClick,
  userType,
}: PaginatedTableProps) => {
  const { trackEvent } = useEventTracking();

  const handleLoadMore = () => {
    fetchNextPage();
    trackEvent(
      click({
        clickTarget: ClickTarget.Button,
        clickText: 'Load more',
      })
    );
  };

  return (
    <>
      {isLoading ? (
        <CustomSpinner
          size={10}
          color={CustomSpinnerColor.PrimaryMagenta}
          style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}
        />
      ) : (
        <>
          {data.pages[0].users.length > 0 ? (
            <UsersTable userType={userType}>
              {data.pages.map((page) =>
                page.users.map((user) => (
                  <UserRow key={user.id} user={user} onUserClick={onUserClick} />
                ))
              )}
            </UsersTable>
          ) : (
            <p className="mt-32 text-center font-suisse-intl text-grayscale-gray">No users found</p>
          )}
          {hasNextPage && (
            <SecondaryButton
              as="button"
              size="small"
              onClick={handleLoadMore}
              className="mb-48 mt-36 w-full bg-white"
            >
              Load more
            </SecondaryButton>
          )}
        </>
      )}
    </>
  );
};

export default PaginatedTable;
